import { useRef, useState } from "react";
import { Paperclip, SendHorizonal } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useChat } from "@/context/ChatContext";
import { VoiceRecorder } from "@/components/chat/VoiceRecorder";

export function Composer({ convId, canPost = true }) {
    const { send } = useChat();
    const [text, setText] = useState("");
    const [progress, setProgress] = useState(null);
    const lastTyping = useRef(0);
    const fileRef = useRef(null);

    const onType = (v) => {
        setText(v);
        if (Date.now() - lastTyping.current > 2500) { lastTyping.current = Date.now(); send({ type: "typing", conversation_id: convId }); }
    };
    const submit = async () => {
        const t = text.trim();
        if (!t) return;
        setText("");
        try { await api.post(`/chat/conversations/${convId}/messages`, { text: t }); }
        catch (e) { setText(t); toast.error(formatApiError(e.response?.data?.detail) || "Message not sent"); }
    };
    const upload = async (file, duration = 0) => {
        if (!file) return;
        if (file.size > 15 * 1024 * 1024) return toast.error("Files must be 15 MB or smaller");
        const fd = new FormData();
        fd.append("file", file);
        fd.append("text", duration ? "" : text.trim());
        if (duration) fd.append("duration", String(duration));
        setProgress(0);
        try {
            await api.post(`/chat/conversations/${convId}/attachments`, fd, { onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || 1)) * 100)) });
            if (!duration) setText("");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
        } finally {
            setProgress(null);
            if (fileRef.current) fileRef.current.value = "";
        }
    };
    const sendVoice = (blob, secs) => {
        const ext = blob.type.includes("mp4") ? "m4a" : blob.type.includes("ogg") ? "ogg" : "webm";
        upload(new File([blob], `voice-note.${ext}`, { type: blob.type || "audio/webm" }), secs);
    };

    if (!canPost) {
        return <div className="border-t border-ink/10 bg-white p-4 text-center text-xs text-ink/50" data-testid="chat-readonly-notice">Only Admin and HR can post in Announcements.</div>;
    }

    return (
        <div className="border-t border-ink/10 bg-white p-3" data-testid="chat-composer">
            {progress !== null && (
                <div className="mb-2 h-1.5 bg-ink/10 rounded-full overflow-hidden" data-testid="chat-upload-progress"><div className="h-full bg-klein transition-[width]" style={{ width: `${progress}%` }} /></div>
            )}
            <div className="flex items-end gap-2">
                <input ref={fileRef} type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="chat-file-input" />
                <button onClick={() => fileRef.current?.click()} disabled={progress !== null} className="p-2.5 rounded-full text-ink/55 hover:bg-ink/5 disabled:opacity-40" title="Send photo or file" data-testid="chat-attach-btn">
                    <Paperclip className="w-5 h-5" />
                </button>
                <textarea value={text} rows={1} onChange={(e) => onType(e.target.value)} placeholder="Type a message"
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
                    className="flex-1 resize-none max-h-32 border border-ink/15 rounded-2xl px-4 py-2.5 text-sm focus:outline-none focus:border-klein" data-testid="chat-message-input" />
                {text.trim() ? (
                    <button onClick={submit} className="p-2.5 rounded-full bg-klein text-white hover:bg-klein/90" data-testid="chat-send-btn">
                        <SendHorizonal className="w-5 h-5" />
                    </button>
                ) : (
                    <VoiceRecorder onRecorded={sendVoice} disabled={progress !== null} />
                )}
            </div>
        </div>
    );
}

export default Composer;
